/* Floating chat bubble for the client side. Polls the thread every 12s.
   Strictly per-client — backend filters by JWT identity so other clients
   can never see this conversation. */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Dynamic API base — must match ClientDashboard so production and preview both work.
// Without this, on https://smartechsystempro.com we'd hit the preview backend
// (different DB) → 401 "Client non trouvé" when the JWT sub references a prod
// user that doesn't exist in preview.
const getApiUrl = () => {
  if (typeof window === "undefined") return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === "smartechsystempro.com" || hostname === "www.smartechsystempro.com") return `${protocol}//${hostname}/api`;
  if (hostname.includes("emergent.host") || hostname.includes("emergentagent.com")) return `${protocol}//${hostname}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
};
const API = getApiUrl();

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("client_token") || ""}` });

// Generate a short ping sound on the fly (no external asset needed).
// Two soft sine tones (660Hz → 880Hz, 0.18s total) — discreet, not intrusive.
const playPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(660, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
    osc.start();
    osc.stop(ctx.currentTime + 0.22);
    osc.onended = () => { try { ctx.close(); } catch (_e) { /* noop */ } };
  } catch (_e) { /* noop */ }
};

const WELCOME_KEY = "smartech_chat_welcome_v1";
const WELCOME_TEXT = "Bonjour ! 👋 Posez votre question ici, l'administrateur vous répond rapidement.";

export default function ClientChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  // Refs used to detect *new* admin messages on each poll without spamming
  // sound/notification on the first load.
  const lastAdminIdRef = useRef(null);
  const seededRef = useRef(false);
  const notifPermAskedRef = useRef(false);

  const fetchThread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/client/chat`, { headers: headers() });
      const msgs = r.data?.messages || [];
      const lastAdmin = [...msgs].reverse().find(m => m.sender_role === "admin");
      // On first fetch, just remember the last admin message — no sound/notif.
      if (!seededRef.current) {
        seededRef.current = true;
        lastAdminIdRef.current = lastAdmin?.id || null;
      } else if (lastAdmin && lastAdmin.id !== lastAdminIdRef.current && !lastAdmin.read_by_client) {
        // A new admin reply arrived since the previous poll.
        lastAdminIdRef.current = lastAdmin.id;
        playPing();
        // System notification when the tab is hidden (and permission granted).
        try {
          if (typeof Notification !== "undefined"
              && Notification.permission === "granted"
              && document.visibilityState !== "visible") {
            const n = new Notification("SMARTECH — Nouveau message", {
              body: (lastAdmin.body || "").slice(0, 120),
              tag: "smartech-chat",
              icon: "/logo192.png",
            });
            n.onclick = () => { try { window.focus(); n.close(); } catch (_e) { /* noop */ } };
          }
        } catch (_e) { /* noop */ }
      }
      setMessages(msgs);
      setUnread(r.data?.unread || 0);
      setAdminOnline(!!r.data?.admin_presence?.online);
      setAdminTyping(!!r.data?.admin_typing);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[chat] fetch err:", e?.message);
    }
  }, []);

  // Polling: 4s when panel is open (responsive typing), 12s when closed.
  // Always paused when tab is hidden to save bandwidth.
  useEffect(() => {
    fetchThread();
    let t = null;
    const interval = open ? 4000 : 12000;
    const start = () => { if (!t) t = setInterval(fetchThread, interval); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    start();
    const onVis = () => (document.visibilityState === "visible" ? (fetchThread(), start()) : stop());
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchThread, open]);

  // Scroll to bottom on new messages while open.
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Mark as read when the panel opens + ask for notification permission once.
  useEffect(() => {
    if (open && unread > 0) {
      axios.post(`${API}/client/chat/mark-read`, {}, { headers: headers() }).catch(() => {});
      setUnread(0);
    }
    if (open && !notifPermAskedRef.current && typeof Notification !== "undefined"
        && Notification.permission === "default") {
      notifPermAskedRef.current = true;
      try { Notification.requestPermission().catch(() => {}); } catch (_e) { /* noop */ }
    }
  }, [open, unread]);

  const [showWelcome, setShowWelcome] = useState(() => {
    try { return !localStorage.getItem(WELCOME_KEY); } catch (_e) { return true; }
  });
  const [adminOnline, setAdminOnline] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);

  // Mark welcome as seen the first time the panel opens.
  useEffect(() => {
    if (open && showWelcome) {
      try { localStorage.setItem(WELCOME_KEY, "1"); } catch (_e) { /* noop */ }
    }
  }, [open, showWelcome]);

  const handleSend = async () => {
    const txt = body.trim();
    if (!txt) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/client/chat`, { body: txt }, { headers: headers() });
      setMessages(prev => [...prev, r.data]);
      setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <button
        data-testid="client-chat-bubble"
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed", right: 16, bottom: 76, zIndex: 99999,
          background: "#0EA5E9", color: "#FFF", border: "none",
          width: 56, height: 56, borderRadius: 28,
          boxShadow: "0 6px 18px rgba(14,165,233,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        }}
        title="Messagerie avec l'administrateur"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
        {!open && unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4, background: "#EF4444",
            color: "#FFF", fontSize: "0.65rem", fontWeight: 800, padding: "0.1rem 0.35rem",
            borderRadius: 999, minWidth: 18, textAlign: "center",
          }}>{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div data-testid="client-chat-panel" style={{
          position: "fixed", right: 16, bottom: 144, zIndex: 99999,
          width: "min(360px, calc(100vw - 32px))", height: "min(480px, 70vh)",
          background: "#FFF", borderRadius: 12, boxShadow: "0 12px 28px rgba(15,23,42,0.25)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          border: "1px solid #E2E8F0",
        }}>
          <div style={{ padding: "0.7rem 0.9rem", background: "linear-gradient(90deg, #0EA5E9, #0369A1)", color: "#FFF" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span data-testid="client-chat-presence-dot" style={{
                width: 9, height: 9, borderRadius: 999,
                background: adminOnline ? "#22C55E" : "#94A3B8",
                boxShadow: adminOnline ? "0 0 0 2px rgba(34,197,94,0.35)" : "none",
                display: "inline-block",
              }} />
              <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>Messagerie SMARTECH</div>
              <span data-testid="client-chat-presence-label" style={{ fontSize: "0.65rem", marginLeft: "auto", opacity: 0.9, fontWeight: 600 }}>
                {adminOnline ? "Admin en ligne" : "Admin hors ligne"}
              </span>
            </div>
            <div style={{ fontSize: "0.7rem", opacity: 0.85, marginTop: 2 }}>Vos échanges avec l'administrateur — privés et sécurisés.</div>
          </div>
          <div ref={scrollerRef} style={{ flex: 1, overflowY: "auto", padding: "0.7rem", background: "#F8FAFC", display: "flex", flexDirection: "column", gap: 6 }}>
            {showWelcome && (
              <div data-testid="client-chat-welcome" style={{
                alignSelf: "flex-start", maxWidth: "85%",
                background: "#FFF", color: "#0F172A",
                padding: "0.55rem 0.7rem", borderRadius: 12,
                fontSize: "0.82rem", lineHeight: 1.45,
                border: "1px solid #E2E8F0", boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}>
                <div style={{ fontWeight: 700, fontSize: "0.72rem", color: "#0369A1", marginBottom: 2 }}>
                  Administrateur SMARTECH
                </div>
                <div>{WELCOME_TEXT}</div>
              </div>
            )}
            {messages.length === 0 && !showWelcome && (
              <div style={{ color: "#64748B", fontSize: "0.78rem", textAlign: "center", padding: "1rem" }}>
                Aucun message pour l'instant.<br/>Posez votre question à l'administrateur, il vous répondra ici.
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} data-testid={`msg-${m.id}`} style={{
                alignSelf: m.sender_role === "client" ? "flex-end" : "flex-start",
                maxWidth: "78%",
                background: m.sender_role === "client" ? "#0EA5E9" : "#FFF",
                color: m.sender_role === "client" ? "#FFF" : "#0F172A",
                padding: "0.45rem 0.65rem", borderRadius: 12,
                fontSize: "0.82rem", lineHeight: 1.4,
                boxShadow: m.sender_role === "client" ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
                border: m.sender_role === "client" ? "none" : "1px solid #E2E8F0",
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                <div>{m.body}</div>
                <div style={{ fontSize: "0.6rem", opacity: 0.7, marginTop: 2, textAlign: "right" }}>
                  {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
          {adminTyping && (
            <div data-testid="client-chat-typing" style={{
              padding: "0.3rem 0.9rem", fontSize: "0.72rem", color: "#0369A1",
              background: "#F0F9FF", borderTop: "1px solid #E0F2FE",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{
                display: "inline-flex", gap: 3,
              }}>
                <span style={{ width: 4, height: 4, background: "#0EA5E9", borderRadius: 999, animation: "smartech-blink 1s infinite" }} />
                <span style={{ width: 4, height: 4, background: "#0EA5E9", borderRadius: 999, animation: "smartech-blink 1s infinite 0.2s" }} />
                <span style={{ width: 4, height: 4, background: "#0EA5E9", borderRadius: 999, animation: "smartech-blink 1s infinite 0.4s" }} />
              </span>
              Admin écrit…
              <style>{`@keyframes smartech-blink { 0%,100% { opacity: 0.3 } 50% { opacity: 1 } }`}</style>
            </div>
          )}
          <div style={{ display: "flex", gap: 6, padding: "0.55rem 0.7rem", borderTop: "1px solid #E2E8F0" }}>
            <textarea
              data-testid="client-chat-input"
              className="form-input"
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              rows={1}
              placeholder="Écrivez votre message…"
              style={{ flex: 1, resize: "none", maxHeight: 80, padding: "0.4rem 0.55rem", fontSize: "0.82rem" }}
            />
            <button
              data-testid="client-chat-send"
              onClick={handleSend}
              disabled={sending || !body.trim()}
              className="btn"
              style={{ background: "#0EA5E9", color: "#FFF", padding: "0.4rem 0.7rem" }}
            ><Send size={14} /></button>
          </div>
        </div>
      )}
    </>
  );
}
